import type { ValidationProfile } from '../editor/validation.ts';
import type { FullRunValidationResult } from './full-run.ts';

export interface RideCheckPresentationIssue {
  readonly id: string;
  readonly code: string;
  readonly kind: 'problem' | 'warning';
  readonly source: 'quick' | 'full';
  readonly message: string;
  readonly sectionId?: string;
  readonly distanceM?: number;
}

export interface RideCheckPresentation {
  readonly status: 'checking' | 'ready' | 'warnings' | 'blocked';
  readonly problemCount: number;
  readonly warningCount: number;
  readonly problems: readonly RideCheckPresentationIssue[];
  readonly warnings: readonly RideCheckPresentationIssue[];
}

function normalizedCode(code: string): string {
  if (code === 'ride_stall') return 'stall';
  if (code === 'positive_vertical_g' || code === 'negative_vertical_g') return 'vertical_g';
  if (code === 'speed_warning') return 'speed';
  return code;
}

function issueKey(issue: {
  readonly code: string;
  readonly sectionId?: string;
  readonly distanceM?: number;
}): string {
  const distanceBucket =
    issue.distanceM === undefined ? 'none' : Math.round(issue.distanceM / 5).toString();
  return `${normalizedCode(issue.code)}:${issue.sectionId ?? 'project'}:${distanceBucket}`;
}

export function buildRideCheckPresentation(
  quick: ValidationProfile,
  full: FullRunValidationResult | null,
  pending: boolean,
): RideCheckPresentation {
  if (pending)
    return {
      status: 'checking',
      problemCount: 0,
      warningCount: 0,
      problems: [],
      warnings: [],
    };

  const problems: RideCheckPresentationIssue[] = [];
  const warnings: RideCheckPresentationIssue[] = [];
  const seen = new Set<string>();

  for (const issue of full?.issues ?? []) {
    // Noticeable intensity is already represented by the ride's intensity
    // rating; it is informational rather than a warning that needs review.
    if (issue.severity === 'info') continue;
    const kind =
      issue.category === 'technical' && issue.severity === 'blocker' ? 'problem' : 'warning';
    const presented: RideCheckPresentationIssue = {
      id: issue.id,
      code: issue.code,
      kind,
      source: 'full',
      message: issue.message,
      ...(issue.sectionId ? { sectionId: issue.sectionId } : {}),
      ...(issue.distanceM !== undefined ? { distanceM: issue.distanceM } : {}),
    };
    seen.add(issueKey(presented));
    (kind === 'problem' ? problems : warnings).push(presented);
  }

  for (const issue of quick.issues) {
    const key = issueKey(issue);
    if (seen.has(key)) continue;
    seen.add(key);
    const kind = issue.severity === 'blocker' ? 'problem' : 'warning';
    const presented: RideCheckPresentationIssue = {
      id: issue.id,
      code: issue.code,
      kind,
      source: 'quick',
      message: issue.message,
      ...(issue.sectionId ? { sectionId: issue.sectionId } : {}),
      ...(issue.distanceM !== undefined ? { distanceM: issue.distanceM } : {}),
    };
    (kind === 'problem' ? problems : warnings).push(presented);
  }

  return {
    status: problems.length ? 'blocked' : warnings.length ? 'warnings' : 'ready',
    problemCount: problems.length,
    warningCount: warnings.length,
    problems,
    warnings,
  };
}
